import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import type { User, AuthContextType, Company, RolePermissions } from '../types/auth';
import { authAPI, companyAPI, clearCache } from '../utils/api';
import { isCompanyRoute } from '../utils/navigation';
import { initPushNotifications, unregisterPush } from '../utils/pushNotifications';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Only log in development
const isDev = import.meta.env.DEV;
const log = (message: string, ...args: any[]) => {
  if (isDev) console.log(message, ...args);
};

const logError = (message: string, ...args: any[]) => {
  if (isDev) console.error(message, ...args);
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [currentCompany, setCurrentCompanyState] = useState<Company | null>(null);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  
  // Role management state
  const [userRolePermissions, setUserRolePermissions] = useState<RolePermissions | null>(null);
  const [userRolePermissionsLoading, setUserRolePermissionsLoading] = useState(false);

  // Ref so loadCompanies can read current company without it being a dependency
  // (avoids loadCompanies re-creation when currentCompany is set inside loadCompanies, which caused 2–3× /companies calls)
  const currentCompanyRef = useRef<Company | null>(null);
  currentCompanyRef.current = currentCompany;

  // Load companies when user is authenticated
  const loadCompanies = useCallback(async () => {
    try {
      log('🏢 Loading companies...');
      setCompaniesLoading(true);
      
      log('🔍 Making API call to /companies');
      const response = await companyAPI.getCompanies();
      
      log('📡 Companies API Response:', response);
      log('✅ Response success:', response.success);
      log('📋 Response data:', response.data);
      log('📊 Number of companies:', response.data?.length || 0);
      
      if (response.success && response.data) {
        setCompanies(response.data);
        log('💾 Set companies in state:', response.data);
        
        // Only auto-select if we don't have a current company and we're not on a company-specific URL
        if (response.data.length > 0 && !currentCompanyRef.current) {
          log('🎯 Checking if company auto-selection is needed...');
          
          // Check if we're currently on a company-specific route
          const isOnCompanyRoute = isCompanyRoute(window.location.pathname);
          
          if (!isOnCompanyRoute) {
            // Only auto-select when not on a company route
            const storedCompany = localStorage.getItem('currentCompany');
            if (storedCompany) {
              try {
                const parsedCompany = JSON.parse(storedCompany);
                log('💾 Found stored company:', parsedCompany);
                // Verify the stored company is still in the list
                const validCompany = response.data.find(c => c.company_id === parsedCompany.company_id);
                if (validCompany) {
                  log('✅ Stored company is valid, using it:', validCompany);
                  setCurrentCompanyState(validCompany);
                } else {
                  log('❌ Stored company not found, using first available:', response.data[0]);
                  // Stored company is no longer valid, select first one
                  setCurrentCompanyState(response.data[0]);
                  localStorage.setItem('currentCompany', JSON.stringify(response.data[0]));
                }
              } catch {
                log('❌ Invalid stored company format, using first available:', response.data[0]);
                // Invalid stored company, select first one
                setCurrentCompanyState(response.data[0]);
                localStorage.setItem('currentCompany', JSON.stringify(response.data[0]));
              }
            } else {
              log('🆕 No stored company, using first available:', response.data[0]);
              // No stored company, select first one
              setCurrentCompanyState(response.data[0]);
              localStorage.setItem('currentCompany', JSON.stringify(response.data[0]));
            }
          } else {
            log('🔗 On company route, skipping auto-selection');
          }
        } else if (response.data.length === 0) {
          log('⚠️ No companies found in response');
        }
      } else {
        log('❌ API response indicates failure or no data');
        log('   - success:', response.success);
        log('   - data exists:', !!response.data);
      }
    } catch (error: any) {
      logError('🚨 Failed to load companies:', error);
      logError('🔍 Error details:', {
        message: error?.message,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data
      });
    } finally {
      setCompaniesLoading(false);
      log('🏁 Companies loading finished');
    }
  }, []);

  useEffect(() => {
    // Check if user is logged in on app start
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('jwttoken');
    
    if (storedUser && token) {
      try {
        const parsedUser = JSON.parse(storedUser);
        log('👤 Setting user from localStorage:', parsedUser);
        setUser(parsedUser);
        initPushNotifications();
        // Load companies after setting user
        log('🚀 User set, now loading companies...');
        loadCompanies();
      } catch (error) {
        // Clear invalid data
        localStorage.removeItem('jwttoken');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('currentCompany');
      }
    }
    setLoading(false);
  }, [loadCompanies]);



  const login = useCallback(async (email: string, password: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors
      
      // Clear all cached data before login to ensure fresh data for new user
      log('🧹 Clearing all cached data before login...');
      clearCache();
      
      const response = await authAPI.login({ email, password });
      
      if (response.success === 1) {
        // Store user data and tokens
        localStorage.setItem('user', JSON.stringify(response.user));
        localStorage.setItem('jwttoken', response.user.jwttoken);
        localStorage.setItem('refresh_token', response.refresh_token);
        
        setUser(response.user);
        initPushNotifications();
        setError(null); // Clear any errors on success
        
        // Load companies after successful login
        log('🚀 Login successful, now loading companies...');
        await loadCompanies();
        
        // Load role permissions if user has a current company
        if (response.user && currentCompany) {
          await loadUserRolePermissions(currentCompany.company_id);
        }
      } else {
        const errorMessage = 'Invalid email or password. Please check your credentials and try again.';
        
        // Set error synchronously
        setError(errorMessage);
        
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      let errorMessage = 'An unexpected error occurred. Please try again.';
      
      // Handle different types of API errors
      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data;
        
        if (status === 401 || status === 403) {
          errorMessage = 'Invalid email or password. Please check your credentials and try again.';
        } else if (status === 422) {
          errorMessage = 'Invalid email or password format. Please check your credentials.';
        } else if (status === 404) {
          errorMessage = 'Login service not found. Please contact support.';
        } else if (status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        } else if (errorData?.message) {
          errorMessage = errorData.message;
        } else if (errorData?.errors) {
          errorMessage = Array.isArray(errorData.errors) 
            ? errorData.errors.join(', ') 
            : 'Validation error. Please check your input.';
      }
      } else if (error.request) {
        // This is likely a CORS or network connectivity issue
        if (error.code === 'ERR_NETWORK') {
          errorMessage = 'Unable to connect to the server. This may be due to network issues or server configuration. Please contact support if the problem persists.';
        } else {
          errorMessage = 'Network error. Please check your internet connection and try again.';
        }
      } else if (error.message) {
        // Use the error message if available (like our own thrown errors)
        errorMessage = error.message;
      }
      
      // Set error before throwing to ensure it's set
      setError(errorMessage);
      
      // Then throw
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [loadCompanies]);

  const logout = useCallback(async () => {
    await unregisterPush();
    // Clear all cached data on logout
    log('🧹 Clearing all cached data on logout...');
    clearCache();
    
    localStorage.removeItem('user');
    localStorage.removeItem('jwttoken');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('currentCompany');
    setUser(null);
    setCurrentCompanyState(null);
    setCompanies([]);
    setError(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Load user role permissions for a specific company
  const loadUserRolePermissions = useCallback(async (companyId: string) => {
    if (!user) return;
    
    try {
      setUserRolePermissionsLoading(true);
      log('🔐 Loading user role permissions for company:', companyId);
      
      const response = await authAPI.getUserCompanyAssignment(companyId, user.id);
      setUserRolePermissions(response.role.role);
      log('✅ User role permissions loaded:', response.role.role);
    } catch (error: any) {
      logError('❌ Failed to load user role permissions:', error);
      // If no assignment found, clear permissions (user has no role in this company)
      setUserRolePermissions(null);
    } finally {
      setUserRolePermissionsLoading(false);
    }
  }, [user]);

  const getScope = useCallback((resource: string): 'all' | 'assigned' => {
    if (user?.superAdminFlag === 1) {
      return 'all';
    }

    if (!userRolePermissions) {
      return 'all';
    }

    const rp = userRolePermissions[resource];
    if (!rp || Array.isArray(rp)) {
      return 'all';
    }

    return rp.scope || 'all';
  }, [user, userRolePermissions]);

  // Check if user has permission for a specific resource and action
  const hasPermission = useCallback((resource: string, action: string): boolean => {
    // Super admins have access to everything
    if (user?.superAdminFlag === 1) {
      return true;
    }
    
    // If no role permissions loaded, deny access
    if (!userRolePermissions) {
      return false;
    }
    
    // Check if the resource exists and if the action is allowed
    const resourcePermissions = userRolePermissions[resource];
    if (!resourcePermissions) {
      return false;
    }

    const actions = Array.isArray(resourcePermissions)
      ? resourcePermissions
      : resourcePermissions.actions;

    return actions.includes(action);
  }, [user, userRolePermissions]);

  const setCurrentCompany = useCallback((company: Company) => {
    setCurrentCompanyState(company);
    localStorage.setItem('currentCompany', JSON.stringify(company));
    
    // Load user role permissions when company changes
    if (user) {
      loadUserRolePermissions(company.company_id);
    }
  }, [user, loadUserRolePermissions]);

  // Load role permissions when current company changes
  useEffect(() => {
    if (user && currentCompany) {
      loadUserRolePermissions(currentCompany.company_id);
    }
  }, [user, currentCompany, loadUserRolePermissions]);

  const value: AuthContextType = {
    user,
    loading,
    error,
    companies,
    currentCompany,
    companiesLoading,
    login,
    logout,
    clearError,
    setCurrentCompany,
    userRolePermissions,
    userRolePermissionsLoading,
    loadUserRolePermissions,
    hasPermission,
    getScope,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 
